// ── Reset Device — configuration & kill switch ────────────────────────────

/**
 * Master kill switch. Set to `false` to disable the whole feature without
 * removing a line of code: the sidebar nav item is hidden and switchPage()
 * never routes to the page. Every other page is completely unaffected.
 *
 * This is the shallow half of the rollback contract — the deep half is that all
 * of this work lives on the `feature/reset-device` branch, cut from the
 * `pre-reset-device` tag.
 */
// Annotated `boolean` rather than inferred: without it TypeScript narrows the
// constant to a literal type and treats the other branch of every guard as dead
// code, which makes flipping the switch noisier than it should be.
export const RESET_DEVICE_ENABLED: boolean = true;

/**
 * Minimum time the matrix loader stays on screen for a *successful* run, so a
 * fast API round-trip doesn't flash past before the operator can register what
 * happened. Failures bypass this entirely and surface immediately.
 */
export const MIN_LOADER_MS = 5_000;

/** Serial parked on the archived record so the real one is freed for reuse. */
export const ARCHIVED_SERIAL = '000-000-0000';

/** VIN written when the operator marks the new record as a spare. */
export const SPARE_VIN = 'SPARE';

/**
 * Max length enforced on the new device's name, client-side.
 *
 * 50 is the commonly-cited MyGeotab limit for `Device.name`; it is not stated in
 * this repo's Geotab reference, so treat it as defensive rather than
 * authoritative. Enforcing it costs nothing and the failure it prevents is
 * expensive: a length rejection from the API would land on the `Add`, i.e.
 * *after* the old record has already been archived.
 */
export const MAX_DEVICE_NAME_LEN = 50;

/**
 * Geotab's built-in asset-type groups. A new device lands in exactly one of
 * these plus whatever customer groups the operator picks.
 *
 * These all match the `Group<Name>Id` pattern that group-hierarchy.ts treats as
 * system groups, so they are filtered out of the customer-group multi-select
 * automatically — the two controls can't collide.
 */
export const ASSET_TYPE_GROUPS = [
  { id: 'GroupVehicleId',   label: 'Vehicle' },
  { id: 'GroupTrailerId',   label: 'Trailer' },
  { id: 'GroupContainerId', label: 'Container' },
  { id: 'GroupEquipmentId', label: 'Equipment' },
] as const;

// ── Which devices may be reset ────────────────────────────────────────────
// Only real Geotab hardware. Resetting is a physical-swap workflow: archive the
// record, free the serial, install the box elsewhere. That only makes sense for
// something you can actually unplug.
//
// Geotab's deviceType values for hardware all begin "Go" — Go4v3, Go5…Go9, Go9B,
// GoCurve, GoAnywhere — so a prefix test is used rather than a fixed list, which
// would silently drop the next model Geotab ships. Everything else is excluded:
// CustomVehicleDevice and CustomDevice (third-party//API-fed records with no
// Geotab box behind them), plus None and Untracked.

/** deviceTypes that pass the "Go" prefix but aren't movable hardware. */
const NON_HARDWARE_GO_TYPES = new Set(['godrive', 'godrivedevice']);

export function isGeotabHardwareDevice(deviceType?: string): boolean {
  const t = (deviceType ?? '').trim().toLowerCase();
  if (!t.startsWith('go')) return false;      // CustomVehicleDevice, CustomDevice, None, Untracked
  return !NON_HARDWARE_GO_TYPES.has(t);       // GoDrive is the phone app, not a box you can move
}
